import type { CalculatorType } from '@/lib/analytics'
import { FERTILIZER_PRODUCTS } from '@/data/fertilizer-products'

/**
 * Maps an `app/tools/<slug>` route directory to the `calculator_type` used in
 * analytics. Kept here (not in the tools manifest) because the branded product
 * routes deliberately collapse onto one calculator_type: per-product breakdowns
 * slice on the `product` input property, never on calculator_type.
 */
const BASE_CALCULATOR_TYPE_BY_TOOL_SLUG: Record<string, CalculatorType> = {
  'aeration-calculator': 'aeration',
  'artificial-turf-cost-calculator': 'artificial-turf-cost',
  'dethatching-calculator': 'dethatching',
  'fertilizer-calculator': 'fertilizer',
  'fertilizer-rate-calculator': 'fertilizer-rate',
  'fertilizer-schedule-calculator': 'fertilizer-schedule',
  'grass-comparison': 'grass-comparison',
  'grub-control-calculator': 'grub-control',
  'herbicide-timing': 'herbicide',
  'lawn-cost-estimator': 'cost',
  'lawn-size-calculator': 'lawn-size-calculator',
  'lime-calculator': 'lime',
  'mowing-calculator': 'mowing',
  'overseeding-calculator': 'overseeding',
  'seeding-calculator': 'seeding',
  'sod-calculator': 'sod',
  'soil-temperature': 'soil-temperature',
  'soil-test-analyzer': 'soil-test-analyzer',
  'spreader-settings': 'spreader-settings',
  'sprinkler-cost-calculator': 'sprinkler-cost',
  'tank-mix-calculator': 'tank-mix',
  'topsoil-calculator': 'topsoil',
  'watering-schedule': 'watering',
  'weed-and-feed-calculator': 'weed-and-feed',
}

export const CALCULATOR_TYPE_BY_TOOL_SLUG: Record<string, CalculatorType> = {
  ...BASE_CALCULATOR_TYPE_BY_TOOL_SLUG,
  ...Object.fromEntries(
    FERTILIZER_PRODUCTS.map((p) => [p.slug, 'fertilizer-schedule' as CalculatorType]),
  ),
}

/** `/tools/lawn-size-calculator?x=1` -> 'lawn-size-calculator'. Null off /tools. */
export function resolveCalculatorType(pathname: string | null): CalculatorType | null {
  if (!pathname) return null
  const match = pathname.match(/^\/tools\/([^/?#]+)/)
  if (!match) return null
  return CALCULATOR_TYPE_BY_TOOL_SLUG[match[1]] ?? null
}
